import React from "react";
import { ArrowLeft, Heart, Shield, Users, Coffee, Home, Globe, Zap, BookOpen, Star } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CHROME_WEB_STORE_URL } from "@/config/extension";

export default function About() {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2 text-muted-foreground hover:text-foreground">
              <ArrowLeft className="h-4 w-4" />
              <span>Back to Home</span>
            </Link>
            <div className="flex items-center space-x-2">
              <Heart className="h-5 w-5 text-primary" />
              <h1 className="text-2xl font-bold">About Us</h1>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 max-w-5xl">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <div className="mb-8">
            <img 
              src="/mail2.webp" 
              alt="Fendly team working together" 
              className="w-32 h-32 rounded-full mx-auto mb-6 object-cover shadow-lg"
            />
          </div>
          <h1 className="text-4xl font-bold mb-4">Hi, we're the Fendly team</h1>
          <p className="text-xl text-muted-foreground mb-6 max-w-3xl mx-auto">
            We're a small team of parents, engineers, and security experts who believe every family deserves 
            to feel safe online. We started Fendly because we wanted to protect our own children, 
            and now we're helping thousands of families do the same.
          </p>
        </div>

        {/* Our Story Section */}
        <section className="mb-16">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6">How it all began</h2>
              <div className="space-y-6 text-muted-foreground">
                <p className="text-lg leading-relaxed">
                  It was a typical evening in 2022. Sarah was helping her 8-year-old with homework when 
                  he accidentally clicked on a link that led to inappropriate content. That moment changed everything.
                </p>
                <p className="text-lg leading-relaxed">
                  As parents ourselves, we knew we weren't alone in this struggle. The internet is amazing, 
                  but it can also be scary for families. We wanted to create something that would protect 
                  our kids without making the internet feel like a prison.
                </p>
                <p className="text-lg leading-relaxed">
                  So we built Fendly - a simple, effective way to keep families safe online. No complicated 
                  settings, no constant monitoring, just peace of mind knowing your kids are protected.
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="bg-gradient-to-br from-primary/10 to-primary/5 p-8 rounded-2xl">
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-4">
                    <Home className="h-8 w-8 text-primary mx-auto mb-2" />
                    <p className="text-sm font-medium">Started at home</p>
                    <p className="text-xs text-muted-foreground">Protecting our own families first</p>
                  </div>
                  <div className="text-center p-4">
                    <Heart className="h-8 w-8 text-primary mx-auto mb-2" />
                    <p className="text-sm font-medium">Built with love</p>
                    <p className="text-xs text-muted-foreground">Every feature designed for families</p>
                  </div>
                  <div className="text-center p-4">
                    <Shield className="h-8 w-8 text-primary mx-auto mb-2" />
                    <p className="text-sm font-medium">Security experts</p>
                    <p className="text-xs text-muted-foreground">Years of experience in cybersecurity</p>
                  </div>
                  <div className="text-center p-4">
                    <Users className="h-8 w-8 text-primary mx-auto mb-2" />
                    <p className="text-sm font-medium">Community driven</p>
                    <p className="text-xs text-muted-foreground">Listening to families' needs</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Meet the Team */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-12">Meet our team</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <Card className="text-center border-0 shadow-lg">
              <CardHeader className="pb-4">
                <div className="w-24 h-24 rounded-full mx-auto mb-4 overflow-hidden border-4 border-blue-100 shadow-lg">
                  <img 
                    src="https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face" 
                    alt="Sarah Chen - Co-founder & CEO" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardTitle className="text-xl">Alan Habib</CardTitle>
                <CardDescription className="text-primary font-medium">Co-founder & CEO</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4">
                  "As a dad of three, I know firsthand how challenging it can be to keep kids safe online. 
                  That's why we built Fendly - to give parents peace of mind."
                </p>
                <div className="flex justify-center space-x-2">
                  <Badge variant="secondary" className="text-xs">Parent</Badge>
                  <Badge variant="secondary" className="text-xs">Security Expert</Badge>
                </div>
              </CardContent>
            </Card>

            <Card className="text-center border-0 shadow-lg">
              <CardHeader className="pb-4">
                <div className="w-24 h-24 rounded-full mx-auto mb-4 overflow-hidden border-4 border-green-100 shadow-lg">
                  <img 
                    src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face" 
                    alt="Moaz Jack - Co-founder & Marketing" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardTitle className="text-xl">Moaz Jack</CardTitle>
                <CardDescription className="text-primary font-medium">Co-founder & Marketing</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4">
                  "I've spent 15 years building systems. With Fendly, I wanted to make 
                  enterprise-grade protection accessible to every family."
                </p>
                <div className="flex justify-center space-x-2">
                  <Badge variant="secondary" className="text-xs">Engineer</Badge>
                  <Badge variant="secondary" className="text-xs">Bachelor</Badge>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Our Values */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-12">What we believe in</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                    <Heart className="h-6 w-6 text-red-600" />
                  </div>
                  <div>
                    <CardTitle>Family First</CardTitle>
                    <CardDescription>Everything we do starts with families</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  We're parents too. We understand the challenges of raising kids in a digital world. 
                  Every feature we build is designed with real families in mind.
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg">
              <CardHeader>
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <Shield className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <CardTitle>Privacy Matters</CardTitle>
                    <CardDescription>Your data belongs to you</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  We believe in protecting families, not tracking them. Your browsing data stays private 
                  and we never sell or share your information.
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg">
              <CardHeader>
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <BookOpen className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <CardTitle>Always Learning</CardTitle>
                    <CardDescription>We listen and improve</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  The internet is constantly changing, and so are we. We listen to our community 
                  and continuously improve our protection.
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg">
              <CardHeader>
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                    <Star className="h-6 w-6 text-purple-600" />
                  </div>
                  <div>
                    <CardTitle>Simple & Effective</CardTitle>
                    <CardDescription>Technology that just works</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  We believe the best technology is invisible. Fendly works quietly in the background, 
                  protecting your family without getting in the way.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Office Life */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-8">A day in our life</h2>
          <div className="bg-muted p-8 rounded-2xl">
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Coffee className="h-8 w-8 text-primary" />
                </div>
                <h3 className="font-semibold mb-2">Morning Standup</h3>
                <p className="text-sm text-muted-foreground">
                  We start each day sharing what we're working on and how we can make the application better.
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Globe className="h-8 w-8 text-primary" />
                </div>
                <h3 className="font-semibold mb-2">Building & Testing</h3>
                <p className="text-sm text-muted-foreground">
                  We spend most of our day improving Fendly and making sure it works perfectly for families, developers, and educators.
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Heart className="h-8 w-8 text-primary" />
                </div>
                <h3 className="font-semibold mb-2">Productive mindset</h3>
                <p className="text-sm text-muted-foreground">
                  We believe in working hard but also taking breaks to recharge and stay creative. Removing distractions is key to our productivity.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Impact */}
        <section className="mb-16">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold mb-4">The impact we're making</h2>
            <p className="text-lg text-muted-foreground">
              Every number represents a person that feels safer online
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold text-primary mb-2">10,000+</div>
              <p className="text-muted-foreground">Families protected</p>
              <p className="text-sm text-muted-foreground mt-2">
                From small towns to big cities, families trust Fendly to keep their kids safe
              </p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-primary mb-2">1M+</div>
              <p className="text-muted-foreground">Threats blocked daily</p>
              <p className="text-sm text-muted-foreground mt-2">
                Our systems work 24/7 to protect families from online threats
              </p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-primary mb-2">99.9%</div>
              <p className="text-muted-foreground">Uptime</p>
              <p className="text-sm text-muted-foreground mt-2">
                Families can count on Fendly to be there when they need it most
              </p>
            </div>
          </div>
        </section>

        {/* Personal Message */}
        <section className="mb-16">
          <Card className="border-0 shadow-lg bg-gradient-to-br from-primary/5 to-primary/10">
            <CardContent className="p-8 text-center">
              <Heart className="h-12 w-12 text-primary mx-auto mb-4" />
              <h3 className="text-2xl font-bold mb-4">Thank you for trusting us</h3>
              <p className="text-lg text-muted-foreground mb-6 max-w-2xl mx-auto">
                Every family that chooses Fendly reminds us why we do what we do. 
                We're honored to be part of your family's online safety journey.
              </p>
              <p className="text-sm text-muted-foreground">
                — The Fendly Team
              </p>
            </CardContent>
          </Card>
        </section>

        {/* Get in Touch */}
        <section className="text-center">
          <h2 className="text-3xl font-bold mb-4">Want to say hello?</h2>
          <p className="text-lg text-muted-foreground mb-6">
            We'd love to hear from you! Whether you have questions, feedback, or just want to chat.
          </p>
          <div className="flex justify-center space-x-4">
            <Button variant="outline" size="lg" asChild>
              <Link to="/help">Get in Touch</Link>
            </Button>
            <Button size="lg" asChild>
              <a href={CHROME_WEB_STORE_URL} target="_blank" rel="noopener noreferrer">
                Try Fendly Free
              </a>
            </Button>
          </div>
        </section>
      </div>
    </div>
  );
}
